import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../../firebase';
import CircularProgress from '@material-ui/core/CircularProgress';

// Redux
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import TextField from '../../stories/general-components/TextField';
import { fetchBusinessData } from '../../redux/actions-v2/coreAction';
import { onAuthStateChanged } from 'firebase/auth';
import axios from 'axios';
import { handleCrossDomainRedirect, generateAuthUrlParams, getVersionRedirectUrl } from '../../utils/crossDomainAuth';
//styles
import AuthLayout from '../../layouts/AuthLayout';
import {
  Typography,
  Divider,
  Tooltip,
  Badge,
} from '@mui/material';
import {
  ArrowDropDownOutlined,
} from '@mui/icons-material';
import GeneralText from '../../stories/general-components/GeneralText';
import Modules from '../../components/@generalComponents/layout/Modules';

const SelectBusiness = () => {
  const { t, i18n } = useTranslation();
  const currentLangCode = i18n.language;
  const dispatch = useDispatch();
  const [businesses, setBusinesses] = useState([]);
  const [businessesUnique, setBusinessesUnique] = useState([]);
  const businessId = localStorage.getItem('businessId');
  const [businessLoaded, setBusinessLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [businessCode, setBusinessCode] = useState('');
  const [showBusinessCodeInput, setShowBusinessCodeInput] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const businessPreference = useSelector((state) => state.core.businessData);
  const currentPlatform = useSelector(state => state.core.currentPlatform);
  const currentUser = useSelector((state) => state.core.user);

  const businessModules = businessPreference?.menu?.filter(
    (module) => module?.section === 'OPERATIONS'
  );

  useEffect(() => {
    if (!businessCode) {
      const fetchBusinesses = async (idToken) => {
        const response = await axios({
          method: 'POST',
          url: 'https://northamerica-northeast1-node-canada.cloudfunctions.net/users/validate',
          headers: {
            'Content-Type': 'application/json',
            'Access-Control-Allow-Origin': '*',
            'Access-Control-Allow-Methods': 'POST,OPTIONS',
            Authorization: `Bearer ${idToken}`,
          },
        });
        setBusinessLoaded(true);
        setBusinesses(response?.data?.access);

        if (businessId) {
          const selectedBusiness = response.data.access.find(
            (business) => business?.businessId === businessId
          );

          if (selectedBusiness && !businessCode) {
            handleBusinessSelect(selectedBusiness);
          }
        }
      };

      const unsubscribe = onAuthStateChanged(auth, async (user) => {
        if (user) {
          let idToken = await user.getIdToken();
          await fetchBusinesses(idToken);
        }
      });

      return () => unsubscribe();
    }
  }, [businessId, dispatch, navigate, t]);

  const fetchBusinessToken = async () => {
    try {
      const idToken = await auth.currentUser.getIdToken();
      setLoading(true);
      const response = await axios({
        method: 'POST',
        url: 'https://northamerica-northeast1-node-canada.cloudfunctions.net/users/validate',
        headers: {
          'Content-Type': 'application/json',
          'Access-Control-Allow-Origin': '*',
          'Access-Control-Allow-Methods': 'POST,OPTIONS',
          Authorization: `Bearer ${idToken}`,
        },
        data: {
          code: businessCode,
        },
      });
      if (response?.data?.access) {
        setBusinessesUnique([response?.data?.access]);
        handleBusinessSelect(response?.data?.access);
      }
    } catch (error) {
      setError(true);
      setLoading(false);
    }
  };

  const handleBusinessSelect = async (business) => {
    if (business?.isLocked) {
      return;
    }

    setLoading(true);
    if (businessCode) {
      setBusinessesUnique([business]);
    }

    // Check if business has a version that requires a different subdomain
    const businessVersion = business?.version || 'app';
    if (businessVersion !== 'app') {
      try {
        // Use secure cookie approach for cross-domain auth
        await handleCrossDomainRedirect(business, businessCode);
        return;
      } catch (error) {
        console.error(`Error preparing v${businessVersion} redirect:`, error);

        // Fallback to URL parameters method
        try {
          const idToken = await auth.currentUser.getIdToken();
          const urlParams = generateAuthUrlParams(
            business,
            businessCode ? businessesUnique : businesses,
            businessCode,
            idToken,
            auth.currentUser.uid
          );

          const redirectUrl = getVersionRedirectUrl(businessVersion);
          window.location.href = `${redirectUrl}?${urlParams}`;
          return;
        } catch (fallbackError) {
          console.error('Error with fallback method:', fallbackError);
          setError(true);
          setLoading(false);
          return;
        }
      }
    }

    // Original logic for non-v2 businesses
    localStorage.setItem('businessId', business.businessId);
    sessionStorage.setItem('businessToken', business.token);
    sessionStorage.setItem(
      'businesses',
      JSON.stringify(businessCode ? businessesUnique : businesses)
    );

    dispatch(fetchBusinessData(business?.businessId, t, null, null, currentPlatform))
      .then(() => {
        setTimeout(() => {
          setLoading(false);
        }, 1500);
      })
      .catch((error) => {
        setError(true);
        setLoading(false);
      });
  };



  useEffect(() => {
    if (businessesUnique?.length > 0) {
      sessionStorage.setItem('businesses', JSON.stringify(businessesUnique));
    }
  });

  return (
    <AuthLayout>
      <div>
        {businesses?.length > 0 && !businessId ? (
          <div className="align-c">
            <div className="d-flex align-c middle-content mb-2">
              <Typography fontWeight={500} variant="h6">
                {t('selectBusiness')}
              </Typography>
              <ArrowDropDownOutlined />
            </div>
            <Divider component="div" />

            <div className="row">
              {!businessCode &&
                businesses?.map((business, idx) => (
                  <div
                    className="col-4 hover p-2"
                    key={idx}
                    onClick={() => handleBusinessSelect(business)}
                  >
                    <Tooltip
                      title={
                        business?.isLocked
                          ? business?.name + ' (' + t('noActivePlan') + ')'
                          : business?.name
                      }
                      placement="right"
                    >
                      <Badge
                        badgeContent={business?.isTrial ? t('trial') : null}
                        color="primary"
                      >
                        <img
                          src={`https://storage.googleapis.com/business-logos-node/${business?.businessId}.png`}
                          height={'45px'}
                          width={'45px'}
                          alt='Logo'
                          style={{
                            borderRadius: '50%',
                            filter: business?.isLocked
                              ? 'grayscale(100%)'
                              : 'none',
                          }}
                          className="hover mt-2"
                        />
                      </Badge>
                    </Tooltip>
                  </div>
                ))}
              <div
                style={{ marginLeft: '5px' }}
                className="align-c col-12 mt-3"
              >
                {showBusinessCodeInput ? (
                  <TextField
                    id="businessCode"
                    name="businessCode"
                    handleKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        fetchBusinessToken();
                      }
                    }}
                    fullWidth
                    type={'codeAccess'}
                    help={error && 'No access found'}
                    error={error}
                    label={t('businessCodeAccess')}
                    onChange={(e) => setBusinessCode(e.target.value)}
                  />
                ) : (
                  <GeneralText
                    text={t('haveBusinessCodeClickHere')}
                    onClick={() => setShowBusinessCodeInput(true)}
                    primary={false}
                    color={'primary'}
                    fontSize="9px"
                    classNameComponent="cursor-pointer hover-underline"
                  />
                )}
              </div>
            </div>
          </div>
        ) : (
          <div>
            <div>
              {businessLoaded && businesses?.length === 0 && (
                <div className="row align-c">
                  <GeneralText
                    size="bold"
                    primary={false}
                    color={'black'}
                    fontSize="11px"
                    text={t('noBusiness') + '.'}
                  />
                  <GeneralText
                    size="regular"
                    primary={false}
                    color={'black'}
                    fontSize="11px"
                    text={t('backLogin')}
                    onClick={() => navigate('/signin')}
                  />
                  <img
                    src="./assets/v3/img/nobusinessesLogin.gif"
                    width="100%"
                    alt="placeholder"
                  />
                </div>
              )}
              {!businessLoaded && businesses?.length === 0 && (
                <div className="mt-5 align-c">
                  <CircularProgress
                    size={'40px'}
                    variant="indeterminate"
                    style={{ marginTop: '20px', marginBottom: '20px' }}
                  />
                  <div>{t('loading')}</div>
                </div>
              )}
              {loading && (
                <div className="mt-5 align-c">
                  <CircularProgress
                    size={'40px'}
                    variant="indeterminate"
                    style={{ marginTop: '20px', marginBottom: '20px' }}
                  />
                  <div>{t('loading')}</div>
                </div>
              )}
              {!loading && businessId && (
                <div className="mt-3 align-left middle-content">
                  <div className="middle-content d-flex mt-1 mb-2">
                    <img
                      src={`https://storage.googleapis.com/business-logos-node/${businessPreference?.id}.png`}
                      height={'32px'}
                      width={'32px'}
                      alt='Logo'
                      style={{
                        borderRadius: '50%',

                      }}

                    />
                    <div>
                      <GeneralText
                        text={currentUser?.displayName}
                        primary={true}
                        fontSize="13px"
                        size="bold"
                        classNameComponent='px-2'
                      />
                      <GeneralText
                        text={businessPreference?.name}
                        primary={true}
                        fontSize="11px"
                        size="medium"
                        classNameComponent='px-2'
                      />     </div>
                  </div>
                  <Modules
                    businessModules={businessModules}
                    isTablet={false}
                    main={true}
                    activeModule={null}
                    isDarkMode={isDarkMode}
                    businessPreference={businessPreference}
                    currentSectionState={'OPERATIONS'} />

                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </AuthLayout>
  );
};

export default SelectBusiness;
